import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import {
  AppRoutes,
  ERROR_INVALID_FORMAT,
  ERROR_OFFICE_MANAGER_EMAIL,
  ERROR_REQUIRED_FIELD,
} from '@/constants';
import { useCountDown } from '@/hooks';
import { ApiResponse } from '@/types';
import { getCleanedPastedText, mergeClasses, showAlertSuccess } from '@/utils';

import { LogoIcon, MailIcon } from '@/assets/icons';
import { Button, Input, InputOTP, InputOTPGroup, InputOTPSlot, Loading } from '@/components/common';
import { LoginScope } from '@/features/logins/enums';
import { useResendOTP, useSendOTP, useVerifyOTP } from '@/features/logins/hooks';

import {
  DEFAULT_EMAIL_DOMAIN,
  DEFAULT_MAX_RESEND_ATTEMPTS,
  DEFAULT_OTP_EXPIRY_SECONDS,
  DEFAULT_OTP_LENGTH,
  DEFAULT_RESEND_OTP_EXPIRY_SECONDS,
} from './OTPLogin.constants';

const OTPLogin = () => {
  const OTP_EXPIRY_SECONDS =
    Number(process.env.REACT_APP_OTP_EXPIRY_SECONDS) || DEFAULT_OTP_EXPIRY_SECONDS;
  const RESEND_OTP_EXPIRY_SECONDS =
    Number(process.env.REACT_APP_RESEND_OTP_EXPIRY_SECONDS) || DEFAULT_RESEND_OTP_EXPIRY_SECONDS;
  const OTP_LENGTH = Number(process.env.REACT_APP_OTP_LENGTH) || DEFAULT_OTP_LENGTH;
  const EMAIL_DOMAIN = process.env.REACT_APP_EMAIL_DOMAIN || DEFAULT_EMAIL_DOMAIN;
  const MAX_RESEND_ATTEMPTS =
    process.env.REACT_APP_MAX_RESEND_ATTEMPTS || DEFAULT_MAX_RESEND_ATTEMPTS;

  const [showOTPInput, setShowOTPInput] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);
  const navigate = useNavigate();

  const { mutateAsync: sendOTPMutation, isPending: isSendOTPPending } = useSendOTP();
  const { mutateAsync: resendOTPMutation, isPending: isResendOTPPending } = useResendOTP();
  const { mutateAsync: verifyOTPMutation, isPending: isVerifyOTPPending } = useVerifyOTP();

  const otpRegex = useMemo(() => new RegExp(REGEXP_ONLY_DIGITS_AND_CHARS), []);
  const loginScope = LoginScope.ADMIN;

  const emailSchema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .required(ERROR_REQUIRED_FIELD)
          .test('no-spaces', ERROR_INVALID_FORMAT, (value) => !/\s/.test(value ?? ''))
          .test('no-at', ERROR_INVALID_FORMAT, (value) => !value?.includes('@'))
          .matches(/^[a-zA-Z0-9._-]+$/, ERROR_INVALID_FORMAT),
      }),
    [],
  );

  const otpSchema = useMemo(
    () =>
      yup.object({
        otp: yup
          .string()
          .required(ERROR_REQUIRED_FIELD)
          .matches(otpRegex, ERROR_INVALID_FORMAT)
          .length(OTP_LENGTH, ERROR_REQUIRED_FIELD),
      }),
    [OTP_LENGTH, otpRegex],
  );

  const emailForm = useForm<{ email: string }>({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const otpForm = useForm<{ otp: string }>({
    resolver: yupResolver(otpSchema),
    defaultValues: { otp: '' },
  });

  const getApiErrorMessage = (error: unknown) => {
    const data = (error as AxiosError)?.response?.data as ApiResponse<string>;
    return data?.errorMessage;
  };

  const {
    isExpired: isOTPExpired,
    formattedTime: otpTime,
    reset: resetOTPCountdown,
    start: startOTPCountdown,
  } = useCountDown({
    initialSeconds: OTP_EXPIRY_SECONDS,
  });

  const {
    isExpired: isResendOTPExpired,
    formattedTime: resendOTPTime,
    reset: resetResendOTPCountdown,
    start: startResendOTPCountdown,
    resetAndStop: resetAndStopResendOTPCountdown,
    isRunning: isResendOTPRunning,
  } = useCountDown({
    initialSeconds: RESEND_OTP_EXPIRY_SECONDS,
  });

  const handleEmailPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    emailForm.setValue('email', getCleanedPastedText(e, { stripDomain: true }), {
      shouldDirty: true,
    });
    if (emailForm.formState.errors.email) emailForm.clearErrors('email');
  };

  const onClear = () => {
    emailForm.reset();
    otpForm.reset();
    setShowOTPInput(false);
    resetOTPCountdown();
    resetResendOTPCountdown();
    setResendAttempts(0);
  };

  const onConfirm = emailForm.handleSubmit(async ({ email }) => {
    const trimmedEmail = email.trim();

    try {
      await sendOTPMutation(`${trimmedEmail}${EMAIL_DOMAIN}`);
      setShowOTPInput(true);
      startOTPCountdown();
      resetAndStopResendOTPCountdown();
      setResendAttempts(0);
      otpForm.reset();
    } catch (error) {
      const apiMessage = getApiErrorMessage(error);
      emailForm.setError('email', {
        type: 'manual',
        message: apiMessage || ERROR_OFFICE_MANAGER_EMAIL,
      });
    }
  });

  const onVerifyOTP = otpForm.handleSubmit(async ({ otp }) => {
    const currentEmail = emailForm.getValues('email').trim();

    try {
      await verifyOTPMutation({
        email: `${currentEmail}${EMAIL_DOMAIN}`,
        otp,
        scope: loginScope,
      });
      showAlertSuccess('Login successful. Welcome to the Admin Portal.');
      navigate(AppRoutes.HOME);
    } catch (error) {
      const apiMessage = getApiErrorMessage(error);
      if (apiMessage) {
        otpForm.setError('otp', { type: 'manual', message: apiMessage });
      }
    }
  });

  const onResendOTP = () => {
    // If countdown is running, prevent resend
    if (isResendOTPRunning && !isResendOTPExpired) return;

    // If countdown expired, reset attempts
    if (isResendOTPExpired && resendAttempts >= Number(MAX_RESEND_ATTEMPTS)) {
      setResendAttempts(0);
      resetAndStopResendOTPCountdown();
    }

    // Check if max attempts reached
    if (resendAttempts >= Number(MAX_RESEND_ATTEMPTS)) {
      otpForm.setError('otp', {
        type: 'manual',
        message: `You can only resend OTP ${MAX_RESEND_ATTEMPTS} times within ${Math.round(
          RESEND_OTP_EXPIRY_SECONDS / 60,
        )} minutes. Please try again later.`,
      });
      return;
    }

    const currentEmail = emailForm.getValues('email').trim();
    resendOTPMutation(`${currentEmail}${EMAIL_DOMAIN}`)
      .then(() => {
        showAlertSuccess('OTP code resent to your email');
        resetOTPCountdown();
        startOTPCountdown();

        const newAttempts = resendAttempts + 1;
        setResendAttempts(newAttempts);

        // Start countdown only on the 3rd (last) attempt
        if (newAttempts >= Number(MAX_RESEND_ATTEMPTS)) {
          resetResendOTPCountdown();
          startResendOTPCountdown();
        }

        if (otpForm.formState.errors.otp) otpForm.clearErrors('otp');
      })
      .catch((error) => {
        const apiMessage = getApiErrorMessage(error);
        emailForm.setError('email', {
          type: 'manual',
          message: apiMessage || ERROR_OFFICE_MANAGER_EMAIL,
        });
      });
  };

  return (
    <div className="w-[450px] max-w-full max-h-auto rounded-2xl p-12 bg-white flex flex-col justify-start items-center gap-10">
      <LogoIcon className="!w-[76px] !h-auto flex-shrink-0" />
      {showOTPInput ? (
        <>
          <div className="flex flex-col justify-start items-center gap-6">
            <h5 className="text-xl font-bold text-brand-primary-darkest">Log In</h5>
            <span className="font-medium text-base text-brand-primary-darkest text-center">
              We have sent an email with your code to {emailForm.watch('email')}
              {EMAIL_DOMAIN}
            </span>
            <div className="flex flex-col justify-start items-center gap-2">
              <Controller
                control={otpForm.control}
                name="otp"
                render={({ field }) => (
                  <InputOTP
                    required
                    label="Please enter OTP code"
                    length={OTP_LENGTH}
                    pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                    value={field.value}
                    onChange={(newOTP) => {
                      field.onChange(newOTP);
                      if (otpForm.formState.errors.otp) otpForm.clearErrors('otp');
                    }}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} isError={!!otpForm.formState.errors.otp} />
                      <InputOTPSlot index={1} isError={!!otpForm.formState.errors.otp} />
                      <InputOTPSlot index={2} isError={!!otpForm.formState.errors.otp} />
                      <InputOTPSlot index={3} isError={!!otpForm.formState.errors.otp} />
                    </InputOTPGroup>
                  </InputOTP>
                )}
              />
              <p
                className={`text-center font-medium text-xs ${
                  isOTPExpired ? 'text-semantic-error-base' : 'text-neutral-n7'
                }`}
              >
                {isOTPExpired ? 'OTP code has expired' : `OTP code will be expired in ${otpTime}`}
              </p>
              {otpForm.formState.errors.otp?.message && (
                <p className="text-center font-medium text-xs text-semantic-error-base whitespace-pre-line">
                  {otpForm.formState.errors.otp?.message?.includes('.')
                    ? otpForm.formState.errors.otp?.message?.replace(/\.\s*/g, '.\n')
                    : otpForm.formState.errors.otp?.message}
                </p>
              )}
            </div>
            <div className="flex flex-col justify-center items-center">
              <span className="font-medium text-base text-brand-primary-darkest text-center">
                Don&apos;t receive the code?
              </span>
              <Button
                variant="link"
                className={mergeClasses(
                  '!font-medium text-base text-brand-primary-base underline pt-0',
                  isResendOTPRunning && 'cursor-not-allowed text-brand-primary-light',
                )}
                onClick={onResendOTP}
              >
                Resend OTP {isResendOTPRunning ? `(${resendOTPTime})` : ''}
              </Button>
            </div>
            <div className="flex flex-row justify-center items-center gap-4">
              <Button variant="cancel" size="lg" className="w-[166px]" onClick={onClear}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="lg"
                className="border-none w-[180px]"
                onClick={onVerifyOTP}
              >
                Confirm
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className=" flex flex-col justify-start items-center gap-6">
            <h5 className="text-xl font-bold text-brand-primary-darkest">Log In</h5>
            <span className="font-medium text-base text-brand-primary-darkest text-center">
              Please enter your Outlook account to receive OTP code
            </span>
            <Input
              required
              value={emailForm.watch('email')}
              onChange={(e) => {
                emailForm.setValue('email', e.target.value, { shouldDirty: true });
                if (emailForm.formState.errors.email) emailForm.clearErrors('email');
              }}
              onPaste={handleEmailPaste}
              error={emailForm.formState.errors.email?.message}
              placeholder="johndoe123"
              label="Outlook account"
              prefix={<MailIcon className="w-5 h-5" />}
              prefixStyle="!left-4"
              postfix={
                <span className="text-sm text-brand-primary-darkest font-medium">
                  {EMAIL_DOMAIN}
                </span>
              }
              postfixStyle="!right-4"
              style={{
                paddingRight: '7rem',
              }}
              className="!w-[354px]"
            />
          </div>
          <Button variant="primary" size="lg" className="border-none w-[180px]" onClick={onConfirm}>
            Send Code
          </Button>
          <Button
            variant="link"
            className="!font-medium !text-base text-brand-primary-base underline !min-h-6"
            onClick={() => {
              navigate(`${AppRoutes.LOGIN_PAGE}?type=password`);
            }}
          >
            Log In with Your Username/Email and Password
          </Button>
        </>
      )}
      {(isSendOTPPending || isResendOTPPending || isVerifyOTPPending) && <Loading fullScreen />}
    </div>
  );
};

export default OTPLogin;
